import { useTranslation } from "../context/Internationalization";
import { axiosGraphQL } from "../api";

const useGraphql = () => {
  const { t } = useTranslation();

  // Helper function to safely parse error messages from GraphQL
  const parseErrorMessage = (error) => {
    try {
      return JSON.parse(error.message);
    } catch {
      // If not JSON, return the original error message wrapped in an object
      return { message: error.message };
    }
  };

  // Centralized error handler for GraphQL errors
  const handleGraphqlErrors = (errors, specificErrorMessage = null) => {
    const errorList = Array.isArray(errors) ? errors : [errors]; // Always work with an array

    if (!errorList || errorList.length === 0) {
      // No errors available, throw a generic error
      throw new Error(`${t("an_error_occurred")}`);
    }

    if (specificErrorMessage) {
      // If a specific message is passed, prioritize it
      throw new Error(`${specificErrorMessage}`);
    }

    // Map and translate each error message
    const concatErrorMessages = errorList
      .map((error) => {
        const errorObject = parseErrorMessage(error);
        return t(errorObject.message) || t("an_error_occurred");
      })
      .join(",\n"); // Join multiple errors with a new line

    throw new Error(concatErrorMessages); // Throw all collected error messages
  };

  // GraphQL query handler
  const handleGraphql = (queryObject, requestOptions) =>
    axiosGraphQL(queryObject, requestOptions)
      .then((response) => {
        // Handle GraphQL-specific errors even if HTTP response is 2xx
        if (response.data.errors) {
          const errorMessages = response.data.errors
            .map((error) => {
              const errorObject = parseErrorMessage(error);
              return t(errorObject.message) || t("an_error_occurred");
            })
            .join(",\n");

          throw new Error(errorMessages);
        }

        return response; // No errors, return the successful response
      })
      .catch((error) => {
        // Handle network, server, or setup errors
        if (error.response) {
          // Server responded with an error status code (e.g., 400, 500)
          if (error.response?.data?.errors) {
            handleGraphqlErrors(error.response.data.errors);
          } else {
            handleGraphqlErrors([error]);
          }
        } else if (error.request) {
          // Request was made but no response received (server down, network issues)
          handleGraphqlErrors([error], t("error_server_not_responding"));
        } else {
          // Something went wrong before the request was even sent
          handleGraphqlErrors([error]);
        }
      });

  // Return the GraphQL executor and error handler
  return { axiosGraphQL: handleGraphql, handleGraphqlErrors };
};

export default useGraphql;
